import React, { useEffect, useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Paper from '@material-ui/core/Paper';

import { loadOrg } from './actions/load-org';
import { updateOrg } from '../action/org/update-org';
import { userSelector } from '../reducers/current-user-selectors';
import { orgSelector } from '../reducers/org-selectors';

import { validateEmail, validateZipcode, validatePhone } from '../businesslogic/form-validators';
import { EmployeeNotifications } from '../employees/employee-notifications';
import { FeatureFlagged, FeatureGatedVariant } from '../feature-gates/feature-flagged';
import { FeatureGates } from '../feature-gates/feature-gates';
import { WeekInReviewNotifications } from '../notification-settings/components/week-in-review-notifications';
import { ConnectToQuickbooks } from '../quickbooks/connect-to-quickbooks';
import { UiBreadcrumbHeader } from '../ui/ui-breadcrumb-header';
import { UiButton } from '../ui/ui-button';
import { UiGreenSwitch } from '../ui/ui-green-switch';
import { ConnectInovaIntegration } from '../webhook-event-subscription/inova/connect-inova-integration';
import { ConnectGenericIntegration } from '../webhook-event-subscription/te-generic/connect-generic-integration';

import { ApplicantStatusSubscriptions } from './applicant-status-subscriptions';
import { WhatsAppConfiguration } from './whatsapp-configuration';

import { quickbooksDescription } from '../config/quickbooks-config';
import { convertPhoneNumberToE164, formatPhoneNumber } from '../util/formatters';
import { HIRING_PERMISSION, EMPLOYEE_PERMISSION } from '../util/permissions';
import { EmbeddableCareersPage } from '../widgets/embeddable-careers-page';
import { FormTextField } from '../widgets/react-hook-form-components/form-text-field';

type InputProps = {
  orgId: number;
};

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  user: userSelector(state),
  org: orgSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrg: () => dispatch(loadOrg(orgId)),
    updateOrg: (update: Partial<Org>) => dispatch(updateOrg(orgId, update)),
  },
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type Props = InputProps & StateProps & DispatchProps;

export const OrganizationSettingsPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ user, org, orgId, actions }: Props) => {
  const userHasHiringPermission = user.hiringPermission === HIRING_PERMISSION.ALL;
  const userHasEmployeePermission = user.employeesPermission !== EMPLOYEE_PERMISSION.NONE;

  const [isToggleLoading, setIsToggleLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    actions.loadOrg();
  });

  const toggleField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setIsToggleLoading(true);
    actions
      .updateOrg({
        [field]: checked,
      })
      .then(() => {
        setIsToggleLoading(false);
      });
  };

  const defaultValues = {
    jobForwardingPhoneNumber: formatPhoneNumber(org?.jobForwardingPhoneNumber) || '',
    employeeForwardingPhoneNumber: formatPhoneNumber(org?.employeeForwardingPhoneNumber) || '',
    indeedEmail: org?.indeedEmail || '',
    indeedZipCode: org?.indeedZipCode || '',
  };

  const {
    control,
    errors,
    formState: { isDirty },
    handleSubmit,
    reset,
  } = useForm({ mode: 'all', defaultValues });

  const onSubmit: SubmitHandler<typeof defaultValues> = async (update) => {
    const { indeedEmail, jobForwardingPhoneNumber, employeeForwardingPhoneNumber, indeedZipCode } =
      update;
    setIsLoading(true);
    actions
      .updateOrg({
        indeedEmail: indeedEmail ? indeedEmail.toLocaleLowerCase() : null,
        jobForwardingPhoneNumber: jobForwardingPhoneNumber
          ? convertPhoneNumberToE164(jobForwardingPhoneNumber)
          : null,
        employeeForwardingPhoneNumber: employeeForwardingPhoneNumber
          ? convertPhoneNumberToE164(employeeForwardingPhoneNumber)
          : null,
        indeedZipCode: indeedZipCode || null,
      })
      .then(() => {
        setIsLoading(false);
        reset({
          ...defaultValues,
          ...update,
        });
      });
  };

  const onReset = () => {
    reset(defaultValues);
  };

  if (!org) {
    return null;
  }
  return (
    <div>
      <UiBreadcrumbHeader
        pageTitle="Organization Settings"
        className="u-position-top-sticky u-background-offWhite"
      />
      <div className="u-maxWidth-800px u-margin-lg">
        {userHasHiringPermission && (
          <>
            <Paper className="u-padding" variant="outlined">
              <div className="typo-label">Application Process Configuration</div>
              <div>
                Use phone interviews
                <UiGreenSwitch
                  checked={org.usesPhoneInterview}
                  onChange={toggleField('usesPhoneInterview')}
                  disabled={isToggleLoading}
                />
              </div>
              <div>
                Use equal opportunity application questions on sex, race, and ethnicity
                <UiGreenSwitch
                  checked={org.usesEqualOpportunityQuestions}
                  onChange={toggleField('usesEqualOpportunityQuestions')}
                  disabled={isToggleLoading}
                />
              </div>
              <div>
                Use self-reporting questions on disability and veteran status
                <UiGreenSwitch
                  checked={org.usesFederalContractorQuestions}
                  onChange={toggleField('usesFederalContractorQuestions')}
                  disabled={isToggleLoading}
                />
              </div>
            </Paper>
            <Paper className="u-padding u-margin-top" variant="outlined">
              <form onSubmit={handleSubmit(onSubmit)}>
                {org.jobPhoneNumber ? (
                  <>
                    <div className="typo-label">Applicant Forwarding Phone Number</div>
                    <div className="typo-subtitle u-margin-y-sm">
                      We will call this number when an in-progress applicant calls the number they
                      are texting for your jobs.
                      <br />
                      You can use a different forwarding number per jobs by navigating to that
                      job&apos;s page, clicking on &quot;Hiring Process,&quot; and then editing
                      contact information in the &quot;Notifications&quot; tab.
                    </div>
                    <FormTextField
                      control={control}
                      name="jobForwardingPhoneNumber"
                      label="Applicant Phone Number"
                      error={errors.jobForwardingPhoneNumber}
                      rules={{ validate: { validatePhone } }}
                      className="u-width-100"
                      textFieldVariant="phone"
                    />
                  </>
                ) : null}
                {org.phoneNumber ? (
                  <>
                    <div className="u-margin-top-xl typo-label">
                      Employee Forwarding Phone Number
                    </div>
                    <div className="typo-subtitle u-margin-y-sm">
                      We will call this number when an employee calls the number they are texting.
                    </div>
                    <FormTextField
                      control={control}
                      name="employeeForwardingPhoneNumber"
                      label="Employee Phone Number"
                      error={errors.employeeForwardingPhoneNumber}
                      rules={{ validate: { validatePhone } }}
                      className="u-width-100"
                      textFieldVariant="phone"
                    />
                  </>
                ) : null}
                <div className="u-margin-top-xl typo-label">Indeed Account Information</div>
                <div className="typo-subtitle">
                  If you would like to sponsor jobs on Indeed, start by giving us your Indeed
                  account email and the zip code for the card on file with Indeed.{' '}
                  <a
                    className="typo-link"
                    href="https://help.teamengine.io/article/49-connecting-team-engine-to-your-companys-indeed-account"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn more
                  </a>
                  .
                </div>
                <FormTextField
                  control={control}
                  name="indeedEmail"
                  label="Indeed Email"
                  error={errors.indeedEmail}
                  rules={{ validate: { validateEmail } }}
                />
                <FormTextField
                  control={control}
                  name="indeedZipCode"
                  label="Indeed Zip Code"
                  error={errors.indeedZipCode}
                  rules={{ validate: { validateZipcode } }}
                />
                <div className="u-flex-center u-margin-top u-justifyContent-flexEnd">
                  <UiButton
                    className="u-margin-right"
                    disabled={!isDirty}
                    variant="secondary"
                    onClick={onReset}
                  >
                    Reset
                  </UiButton>
                  <UiButton type="submit" disabled={!isDirty || isLoading}>
                    Save
                  </UiButton>
                </div>
              </form>
            </Paper>
          </>
        )}
        {userHasEmployeePermission && <EmployeeNotifications orgId={orgId} />}
        <WeekInReviewNotifications orgId={orgId} />
        <ApplicantStatusSubscriptions orgId={orgId} />
        {(user.isAccountAdmin || user.isAdmin) && (
          <>
            <div className="u-margin-top-xl typo-h5 typo-bold">Integrations</div>
            <div className="card card-body u-margin-bottom-lg">
              <div className="typo-label u-margin-bottom-lg">Inova</div>
              <ConnectInovaIntegration org={org} />
            </div>
            <div className="card card-body">
              <div className="typo-label u-margin-bottom-lg">Quickbooks</div>
              <div className="typo-subtitle u-maxWidth-750px u-margin-bottom-lg">
                {quickbooksDescription}
              </div>
              <ConnectToQuickbooks org={org} />
            </div>
            <div className="card card-body u-margin-top-lg">
              <div className="typo-label u-margin-bottom-lg">Webhook</div>
              <ConnectGenericIntegration org={org} />
            </div>
          </>
        )}
        <div className="u-margin-top-xl typo-h5 typo-bold">
          Connect Team Engine Jobs to Your Website
        </div>
        <div className="u-maxWidth-750px typo-subtitle">
          To add a dynamic list of Team Engine jobs to your website, copy the HTML code snippet
          below. Provide the snippet to your IT or website contact for inclusion on the correct page
          on your website.{' '}
          <a
            className="typo-link"
            href="https://help.teamengine.io/article/66-adding-team-engine-jobs-to-your-website"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </div>
        <div className="u-margin-bottom-xl u-maxWidth-750px">
          <EmbeddableCareersPage org={org} />
        </div>
        <FeatureFlagged
          flagName={FeatureGates.WHATSAPP_SIGN_IN_WITH_FACEBOOK}
          variant={FeatureGatedVariant.SHOW_ON_FLAG}
        >
          <div className="u-margin-top-xl typo-h5 typo-bold">Enable WhatsApp Messaging</div>
          <div className="u-maxWidth-750px typo-subtitle">
            To use WhatsApp, you first have to sign in with your Facebook Business Manager account.
            You&apos;ll need to make one if you do not have one currently.
          </div>
          <div className="u-margin-bottom-xl u-maxWidth-750px">
            <WhatsAppConfiguration org={org} />
          </div>
        </FeatureFlagged>
      </div>
    </div>
  );
});
